import { QueryClient } from '@tanstack/react-query';
import type { QueryRecoveryState } from '@/lib/http/queryUtils';
import { handleMutationError } from './handleMutationError';

const ONE_HOUR = 60 * 60 * 1000;

/**
 * Staleness pinned on the company-scoped queries the app shell needs on every authenticated boot.
 * They are prefetched at t=0 (see `prefetchCompanyBootstrapQueries`), so with the global
 * `staleTime: 0` every one of them would be refetched the moment its hook mounts a few hundred
 * milliseconds later - the prefetch would buy nothing but a duplicate request. Mutations that
 * change this data invalidate it explicitly, which ignores staleness.
 */
export const BOOTSTRAP_QUERY_STALE_TIME = 2 * 60 * 1000;

const SHELL_GATE_RECOVERY_MS = 30_000;

export const recoverShellGate = (query: QueryRecoveryState): number | false =>
	query.state.status === 'error' && query.state.data === undefined ? SHELL_GATE_RECOVERY_MS : false;

export const SHELL_GATE_QUERY_DEFAULTS = {
	staleTime: BOOTSTRAP_QUERY_STALE_TIME,
	retryOnMount: false,
	refetchInterval: recoverShellGate,
} as const;

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnReconnect: true,
			staleTime: 0,
			retry: false,
			refetchOnWindowFocus: true,
			gcTime: ONE_HOUR,
		},
		mutations: {
			onError: handleMutationError,
		},
	},
});
